import React from 'react';
import { Footer } from './Footer';
import { InteractiveNotchDiagramResponsive } from './InteractiveNotchDiagramResponsive';
import { MiniNotchVisualizer } from './MiniNotchVisualizer';
import { hapticFeedback } from '../utils/haptic';

interface EntryPageProps {
  onStartAssessment: () => void;
  onKnowFrequency: () => void;
}

export const EntryPage: React.FC<EntryPageProps> = ({ onStartAssessment, onKnowFrequency }) => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-purple-600 via-purple-500 to-pink-500 text-white py-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold mb-6">
            Notched Sound Therapy
          </h1>
          <p className="text-xl sm:text-2xl mb-4 opacity-90 max-w-3xl mx-auto">
            Research-based white noise that removes your tinnitus frequency
          </p>
          
          {/* Mini Notch Visualizer */}
          <div className="mb-8">
            <MiniNotchVisualizer className="mx-auto" />
            <p className="text-sm opacity-75 mt-2">Your frequency removed from the sound spectrum</p>
          </div>
          
          <div className="flex flex-col gap-3 sm:gap-4 sm:flex-row sm:justify-center max-w-md mx-auto sm:max-w-none">
            <button
              onClick={() => {
                hapticFeedback.buttonTap();
                onStartAssessment();
              }}
              className="medical-button w-full sm:w-auto px-10 py-5 sm:py-4 bg-white text-purple-600 font-semibold rounded-full hover:bg-gray-50 transition-all duration-200 shadow-lg text-xl sm:text-lg tap-highlight-none transform hover:scale-105"
            >
              Find Your Frequency →
            </button>
            <button
              onClick={() => {
                hapticFeedback.buttonTap();
                onKnowFrequency();
              }}
              className="medical-button w-full sm:w-auto px-8 py-3 sm:py-4 bg-transparent text-white font-semibold rounded-full border-2 border-white/50 sm:border-white hover:bg-white hover:text-purple-600 transition-all duration-200 text-base sm:text-lg tap-highlight-none"
            >
              I Know My Frequency
            </button>
          </div>
          <div className="mt-8 flex flex-wrap justify-center gap-6 text-sm">
            <span className="flex items-center gap-2">
              <span className="text-white/80">✓</span> Quick 5-minute assessment
            </span>
            <span className="flex items-center gap-2">
              <span className="text-white/80">✓</span> Based on acoustic research
            </span>
            <span className="flex items-center gap-2">
              <span className="text-white/80">✓</span> No registration required
            </span>
          </div>
        </div>
      </section>


      {/* Your Complete Journey Section */}
      <section id="complete-journey" className="py-16 bg-gradient-to-b from-white to-purple-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-gray-800 mb-12">
            How Notched Sound Therapy Works
          </h2>
          
          {/* Journey Timeline */}
          <div className="max-w-5xl mx-auto">
            {/* Step 1: Frequency Detection */}
            <div className="flex flex-col md:flex-row gap-6 mb-12 items-start">
              <div className="flex-shrink-0 w-full md:w-auto">
                <div className="bg-gradient-to-br from-purple-500 to-pink-500 text-white rounded-full w-16 h-16 flex items-center justify-center text-2xl font-bold mx-auto md:mx-0">
                  1
                </div>
              </div>
              <div className="flex-grow text-center md:text-left">
                <h3 className="text-2xl font-semibold text-purple-800 mb-3">
                  1. Identify Your Frequency
                </h3>
                <p className="text-purple-700 mb-4">
                  A brief audio assessment helps determine your specific tinnitus frequency using 
                  comparative listening – no technical knowledge required.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-6 text-center border border-purple-100">
                    <div className="text-3xl mb-2 flex justify-center">🎧</div>
                    <h4 className="font-semibold text-purple-700 mb-1">Listen</h4>
                    <p className="text-sm text-purple-600">
                      Compare reference tones
                    </p>
                  </div>
                  <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-6 text-center border border-purple-100">
                    <div className="text-3xl mb-2 flex justify-center">🎯</div>
                    <h4 className="font-semibold text-purple-700 mb-1">Match</h4>
                    <p className="text-sm text-purple-600">
                      Simple comparison method
                    </p>
                  </div>
                  <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-6 text-center border border-purple-100">
                    <div className="text-3xl mb-2 flex justify-center">✓</div>
                    <h4 className="font-semibold text-purple-700 mb-1">Results</h4>
                    <p className="text-sm text-purple-600">
                      Your precise frequency
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Arrow Connector */}
            <div className="flex justify-center my-8">
              <svg width="30" height="50" viewBox="0 0 30 50" className="text-purple-400">
                {/* Vertical line */}
                <rect x="13" y="0" width="4" height="35" fill="currentColor" />
                {/* Arrow head */}
                <path d="M 15,50 L 0,35 L 13,35 L 13,30 L 17,30 L 17,35 L 30,35 Z" fill="currentColor" />
              </svg>
            </div>

            {/* Step 2: Notched White Noise */}
            <div className="flex flex-col md:flex-row gap-6 items-start">
              <div className="flex-shrink-0 w-full md:w-auto">
                <div className="bg-gradient-to-br from-purple-500 to-pink-500 text-white rounded-full w-16 h-16 flex items-center justify-center text-2xl font-bold mx-auto md:mx-0">
                  2
                </div>
              </div>
              <div className="flex-grow text-center md:text-left">
                <h3 className="text-2xl font-semibold text-purple-800 mb-3">
                  2. Experience Notched Sound
                </h3>
                <p className="text-purple-700 mb-4">
                  The system generates white noise with your specific frequency removed – 
                  creating a "notch" in the sound spectrum that may help reduce tinnitus perception.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Interactive Diagram Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <InteractiveNotchDiagramResponsive />
          </div>
        </div>
      </section>

      {/* Simple Explanation Section */}
      <section className="py-16 bg-purple-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-purple-800 mb-8">
              The Science Behind It
            </h2>
            <p className="text-lg text-purple-700 mb-6">
              Notched sound therapy removes your tinnitus frequency from white noise. 
              Research indicates this targeted approach may help your brain gradually reduce its focus on that specific tone.
            </p>
            <p className="text-lg text-purple-700 mb-8">
              This acoustic therapy method has shown promise in clinical studies 
              and is now available here at no cost.
            </p>
          </div>
        </div>
      </section>




      
      {/* Footer */}
      <Footer />
    </div>
  );
};